import { Button, cn } from '@blink/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, MapPinOff, Play, Square } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorAlert } from '@/components/common';
import { currentLocation } from '@/components/route-bits';
import { useI18n } from '@/i18n';
import { api, type ShiftState } from '@/lib/api';
import { startTracking, stopTracking } from '@/lib/gps';
import { HelpTip } from '@/components/help-tip';

export function useShift() {
  return useQuery({ queryKey: ['courier', 'shift'], queryFn: () => api<ShiftState>('/courier/shift'), refetchInterval: 5 * 60_000 });
}

/** Location is taken only while the shift is open (P3-11); mounted once for the signed-in screens. */
export function ShiftTracker() {
  const shift = useShift();
  const on = !!shift.data?.shift;
  // not stopped when a screen closes (each screen mounts this): only when the shift ends or on sign-out
  useEffect(() => {
    if (shift.data === undefined) return;
    if (on) startTracking();
    else stopTracking();
  }, [on, shift.data]);
  return null;
}

/** Home: start or end the shift, with time and GPS. */
export function ShiftCard() {
  const { t, f, formatTime } = useI18n();
  const queryClient = useQueryClient();
  const shift = useShift();
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [noGps, setNoGps] = useState(false);
  const change = useMutation({
    mutationFn: async (to: 'start' | 'end') => {
      const location = await currentLocation();
      setNoGps(!location);
      return api(`/courier/shift/${to}`, { method: 'POST', body: { location } });
    },
    onSuccess: () => {
      setConfirmEnd(false);
      void queryClient.invalidateQueries({ queryKey: ['courier', 'shift'] });
    },
  });
  const s = shift.data?.shift;
  if (shift.isLoading) return null;

  return (
    <div className={cn('space-y-2 rounded-xl border p-3', s ? 'bg-success-soft/50' : 'bg-card')} data-testid="shift-card" data-on={!!s}>
      <div className="flex items-center gap-3">
        <Clock className={cn('h-5 w-5 shrink-0', s ? 'text-success' : 'text-muted-foreground')} />
        <p className="flex-1 text-sm font-semibold" data-testid="shift-status">
          {s ? f(t.shift.onSince, { time: formatTime(s.startedAt) }) : t.shift.off}
        </p>
        <HelpTip id="courier.shift" className="text-muted-foreground" />
        {s ? (
          <Button size="sm" variant={confirmEnd ? 'destructive' : 'outline'} disabled={change.isPending} onClick={() => (confirmEnd ? change.mutate('end') : setConfirmEnd(true))} data-testid="shift-end">
            <Square className="h-4 w-4" />
            {confirmEnd ? t.shift.endSure : t.shift.end}
          </Button>
        ) : (
          <Button size="sm" disabled={change.isPending} onClick={() => change.mutate('start')} data-testid="shift-start">
            <Play className="h-4 w-4" />
            {t.shift.start}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{s ? t.shift.trackingOn : t.shift.trackingOff}</p>
      {noGps ? (
        <p className="flex items-center gap-1 text-xs text-warning-foreground">
          <MapPinOff className="h-4 w-4" />
          {t.shift.noGps}
        </p>
      ) : null}
      <ErrorAlert error={change.error} />
    </div>
  );
}
